import { StoryblokComponent, storyblokEditable } from 'gatsby-source-storyblok';
import React, { useContext, memo, useEffect } from 'react';
import { LayoutContext } from '../../contexts/LayoutContext';
import { PageContext } from '../../contexts/PageContext';
import { Blok } from '../../interfaces/Blok.interface';
import TestingGrid from '../TestingGrid/TestingGrid.molecule';
import { StoryblokNode } from '../../interfaces/StoryblokNode.interface';
import { StoryblokArticleAuthor } from '../../interfaces/StoryblokArticleAuthor.interface';
import { ArticleContext } from '../../contexts/ArticleContext';
import { getHeroBloksFromBody } from '../../utils/getHeroBloksFromBody/getHeroBloksFromBody.util';
import BreadcrumbBar from '../BreadcrumbBar/BreadcrumbBar';
import { getNonHeroBloksFromBody } from '../../utils/getNonHeroBloksFromBody/getNonHeroBloksFromBody.util';
import ArticleStructuredData from './ArticleStructuredData';
import { StoryblokAsset } from '../../interfaces/StoryblokAsset.interface';
import {
  DataLayerData,
  pushToDataLayer,
} from '../../utils/pushToDataLayer/pushToDataLayer.util';
import { Breadcrumb } from '../../interfaces/Breadcrumbs.interface';
import { getUnixTimestampFromDateString } from '../../utils/getUnixTimestampFromDateString/getUnixTimestampFromDateString.util';
import Cookies from 'js-cookie';
import moment from 'moment';

interface ArticleBlokProps extends Blok {
  body?: Blok[] | null;
  show_share_page_bar?: boolean;
  show_contact_bar: boolean;
  category_tags: string[];
  subcategory_tags: string[];
  read_time: string;
  authors: StoryblokNode<StoryblokArticleAuthor>[];
  published_date_override?: string;
  updated_date_override?: string;
  preview_card_heading: string;
  preview_card_image: StoryblokAsset;
  first_published_at: string;
  published_at: string;
  updated_at: string;
  slug: string;
  lead_source_reference: string;
}

interface ArticleProps {
  blok: ArticleBlokProps;
}

const Article: React.FC<ArticleProps> = ({ blok }) => {
  const { header, footer, contact_bar, share_page_bar, breadcrumbs } =
    useContext(LayoutContext);
  const heroBloks: Blok[] = getHeroBloksFromBody(blok.body || []);
  const nonHeroBloks: Blok[] = getNonHeroBloksFromBody(blok.body || []);

  /*-- 
    fire off the data layer stuff to enable analysis
    of data when user arrives on the page
  --*/
  useEffect(() => {
    const data: { [key: string]: string } = {
      article_title: breadcrumbs?.label || '',
    };

    breadcrumbs?.breadcrumbs.forEach(
      (breadcrumb: Breadcrumb, index: number) => {
        data[`article_level_${index + 1}`] = breadcrumb.label;
      }
    );

    pushToDataLayer({
      event: 'article_view',
      data,
    });

    // pushToDataLayer({
    //   event: 'article_view',
    //   data: {
    //     article_title: breadcrumbs?.label || '',
    //   },
    // });
  }, []);

  if (!Cookies.get('lead_source_reference') && blok?.lead_source_reference) {
    Cookies.set('lead_source_reference', blok.lead_source_reference, {
      expires: 7,
    });
  }

  return (
    <PageContext.Provider
      value={{
        headerIsTransparent: false,
        isHeaderSearchFunctionalityRequired: true,
        showContactBar: blok.show_contact_bar,
      }}
    >
      <ArticleContext.Provider
        value={{
          category_tags: blok.category_tags,
          subcategory_tags: blok.subcategory_tags,
          authors: blok.authors,
          readTime: blok.read_time,
          publishedDateOverride: blok.published_date_override,
          publishedAt: blok.published_at,
          updatedDate: blok.updated_at,
          updatedDateOverride: blok.updated_date_override,
        }}
      >
        <ArticleStructuredData
          name={blok.preview_card_heading}
          image={blok.preview_card_image}
          datePublished={blok.published_date_override || blok.published_at}
          authors={blok.authors}
          dateModified={blok.published_at}
          slug={blok.slug}
        />
        <div className="flex flex-col h-screen justify-between">
          {header && (
            <StoryblokComponent blok={header.content} key={header._uid} />
          )}
          <main className="mb-auto" {...storyblokEditable(blok)}>
            {heroBloks.map((heroBlok: Blok) => (
              <StoryblokComponent blok={heroBlok} key={heroBlok._uid} />
            ))}
            {breadcrumbs && <BreadcrumbBar breadcrumbs={breadcrumbs} />}
            {nonHeroBloks.map((nonHeroBlok: Blok) => (
              <StoryblokComponent blok={nonHeroBlok} key={nonHeroBlok._uid} />
            ))}
            {blok.show_share_page_bar && share_page_bar && (
              <StoryblokComponent
                blok={share_page_bar.content}
                key={share_page_bar._uid}
              />
            )}
            {blok.show_contact_bar && contact_bar && (
              <StoryblokComponent
                blok={contact_bar.content}
                key={contact_bar._uid}
              />
            )}
          </main>
          {footer && (
            <StoryblokComponent blok={footer.content} key={footer._uid} />
          )}
        </div>
        <TestingGrid />
      </ArticleContext.Provider>
    </PageContext.Provider>
  );
};

export default memo(Article);
